import { Link } from 'react-router-dom';
import { ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { PageHero } from '../shared/PageHero';
import { CreatorRail } from './CreatorRail';
import { AboutShelf } from './AboutShelf';
import { VisitBlock } from '../shared/VisitBlock';
import { placeholderImages } from '../../data/placeholderImages';
import { founder, salon } from '../../data/content';
import { pageActions } from '../../data/pageActions';
import { usePages } from '../../contexts/SiteContext';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { mediaOr } from '../../core/rules/media';
import { useOverlayActions } from '../../contexts/StoreContext';
import { fill } from '../../utils/copy';
import { quietLink } from '../../ui/sections';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';

/** Alleen feiten die Sarah kan onderbouwen; geen opgeklopte getallen. Reactietijd staat op Contact. */
const facts = [{
  value: policyCopy.experience,
  label: 'in de huidzorg'
}, {
  value: 'Eén',
  label: 'cliënt tegelijk, zonder haast'
}];

/**
 * Over Sarah, als één verhaal (docs/VORMGEVING.md §3). Eén vraag: waarom zou ik háár vertrouwen
 * met mijn huid? Wie (opening, sand) · hoe het begon, met feiten en opleidingen (zelfde vlak) ·
 * waar ik voor sta (klei, het ene bewegende beeld) · haar eigen plank (shell) · wie het ook
 * gebruiken: pers en vaste cliënten (sand) · kom kennismaken (blush). Geen blok herhaalt een ander.
 *
 * Collega's staan op de salonpagina, adres en tijden op Contact, de reactietijd ook.
 */
export function About() {
  const pages = usePages();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.about;
  const portrait = mediaOr(copy.hero.media, {
    image: founder.portraitPalm,
    alt: `${founder.name}, ${founder.role}`,
    focus: {
      x: 50,
      y: 30
    }
  });
  const atWork = mediaOr(copy.philosophy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling`
  });
  // De eerste alinea draagt de opening; de rest is "hoe het begon", met de laatste als slotzin.
  const [lead, ...rest] = copy.hero.paragraphs.filter(p => p.trim());
  const closing = rest.length > 1 ? rest[rest.length - 1] : undefined;
  const story = closing ? rest.slice(0, -1) : rest;
  const credentials = copy.credentials.items.filter(c => c.trim());
  return <>
      {/* Opening: zelfde opbouw als salon en protocollen; het eerste scherm is het portret en haar belofte. */}
      <PageHero titleId="about-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} data-id="element-5551" data-mp="1r17hocwvu9ff" />} body={lead ? fill(lead) : undefined} media={portrait} byline={{
      lead: <span className="text-ink" data-id="element-5552" data-mp="2xg8awauiwdib">{founder.name}</span>,
      sub: `${founder.role} · ${salon.city}`
    }} actions={/* Wie haar vertrouwt, wil zien wat ze kiest: de collectie is de hoofdknop, de afspraak de tweede. */
    <>
            <Link to="/webshop" className={btnPrimaryLg} data-id="element-5553" data-mp="37kykn2w64lx3">
              {pageActions.viewCollection}
            </Link>
            {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnSecondaryLg} data-id="element-5554" data-mp="32sf2dsmjd2ly">
                {fill(copy.hero.cta)}
              </button>}
          </>} data-id="element-5550" data-mp="1i1ucvxjpe911" />

      {/* Hoe het begon: het verhaal links, het bewijs rechts. Hetzelfde zandvlak als de opening. */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-28" aria-labelledby="about-story" data-id="element-5555" data-mp="2owjsude113v8">
        <div className="mx-auto grid max-w-content gap-12 border-t border-ink/15 px-6 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pt-16" data-id="element-5556" data-mp="2jx6vgypj4eee">
          <div className="max-w-2xl lg:col-span-7" data-id="element-5557" data-mp="i499nmu66m2y">
            <h2 id="about-story" className="type-sub text-ink" data-id="element-5558" data-mp="i2y3xhh5z497">
              Hoe het begon
            </h2>
            {story.length > 0 && <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-muted" data-id="element-5559" data-mp="l4uazzq3y75g">
                {story.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} data-id="element-5560" data-mp="q346kbfqv9rl" data-mpi={i}>{fill(p)}</p>)}
              </div>}
            {closing && <p className="mt-8 type-title italic text-ink" data-id="element-5561" data-mp="c6qnwyzh52bj">{fill(closing)}</p>}
          </div>

          <div className="lg:col-span-5" data-id="element-5562" data-mp="g4g26fgomt6d">
            {/* Getal + uitleg lezen als één zin: "25 jaar in de huidzorg". */}
            <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1 lg:gap-0 lg:divide-y lg:divide-ink/15" data-id="element-5563" data-mp="75ksuuicd2yu">
              {facts.map(f => <li key={f.label} className="lg:flex lg:items-baseline lg:gap-5 lg:py-5 lg:first:pt-0" data-id="element-5564" data-mp="3on40ee2n7bd5">
                  <span className="block type-sub text-ink lg:w-40 lg:shrink-0" data-id="element-5565" data-mp="3tlxfgaxkvaff">{f.value}</span>
                  <span className="mt-1.5 block text-[14px] leading-snug text-muted lg:mt-0" data-id="element-5566" data-mp="3fmpdjaa15o7j">{f.label}</span>
                </li>)}
            </ul>
            {credentials.length > 0 && <div className="mt-8 border-t border-ink/15 pt-6" data-id="element-5567" data-mp="30zgytzhij0qt">
                <h3 className="text-[14px] text-muted" data-id="element-5568" data-mp="2w04afaaztlm4">{fill(copy.credentials.heading)}</h3>
                <ul className="mt-3 space-y-2" data-id="element-5569" data-mp="39z41sppw4elz">
                  {credentials.map(c => <li key={c} className="text-[15px] leading-snug text-ink" data-id="element-5570" data-mp="35y0jzxoa88i4">
                      {fill(c)}
                    </li>)}
                </ul>
              </div>}
          </div>
        </div>
      </section>

      {/* Waar ik voor sta: het ene klei-vlak. De foto beweegt licht binnen zijn kader. */}
      <section id="filosofie" className="scroll-mt-24 bg-clay text-shell" aria-label="Filosofie" data-id="element-5571" data-mp="3tm1psyu2tatu">
        <div className="grid lg:grid-cols-12" data-id="element-5572" data-mp="3opkzbxzemkdl">
          <figure className="flex flex-col justify-center px-6 py-20 sm:px-10 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-5573" data-mp="6in5soyyjy4c">
            <p className="text-[14px] text-shell" data-id="element-5574" data-mp="1ldhxdr7vuim">{fill(copy.philosophy.eyebrow)}</p>
            <blockquote className="mt-5 max-w-2xl type-section" data-id="element-5575" data-mp="2vd3fk2wj5trv">
              “{fill(copy.philosophy.claim)}.”
            </blockquote>
            <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-shell" data-id="element-5576" data-mp="34c8e3983dn41">{fill(copy.philosophy.body)}</p>
            <figcaption className="mt-8 type-title italic text-blush" data-id="element-5577" data-mp="392osavjqlcrg">{founder.firstName}</figcaption>
          </figure>
          <ParallaxMedia media={atWork} sizes="(min-width: 1024px) 42vw, 100vw" range={40} className="aspect-[4/5] w-full sm:aspect-[16/11] lg:col-span-5 lg:aspect-auto lg:h-full lg:min-h-[640px]" data-id="element-5578" data-mp="3e2t78h4zxzfk" />
        </div>
      </section>

      <AboutShelf copy={copy.shelf} data-id="element-5579" data-mp="3s2b3tiojagzh" />

      <CreatorRail heading={copy.clients.heading} body={copy.clients.body} data-id="element-5580" data-mp="3s19ficiibxzt" />

      {/* Kom kennismaken: het gedeelde "kom langs"-blok. Eén knop; de salonpagina en WhatsApp als rustige links. */}
      <VisitBlock titleId="about-salon" eyebrow={copy.salon.eyebrow} heading={copy.salon.heading} body={copy.salon.body} cta={copy.salon.cta} media={{
      image: placeholderImages.salonRoom,
      alt: 'Sfeerbeeld: een rustige behandelkamer met linnen in daglicht'
    }} motion="still" links={<>
            {copy.salon.salonCta && <Link to="/behandelingen" className={`${quietLink} text-ink`} data-id="element-5582" data-mp="1cef9z83n378r">
                {fill(copy.salon.salonCta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5583" data-mp="2910ochla52uz" />
              </Link>}
            {copy.salon.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-5584" data-mp="2013v02ny3ftl">
                <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5585" data-mp="1v2txvminu55c" />
                {fill(copy.salon.secondaryCta)}
                <span className="sr-only" data-id="element-5586" data-mp="jo6s7hd0moup">{shopLabels.viaWhatsApp}</span>
              </a>}
          </>} data-id="element-5581" data-mp="1xojny1fm30vl" />
    </>;
}